import ko from "knockout";
import { CloseCallback } from "flow/api/types";
import MyFlow from "flow/MyFlow";
import config from "flow/config";
import manifestManager from "flow/utils/manifestManager";
import viewCache from "flow/utils/viewCache";
import viewHtml from "./LogoutCountdownDialog.html";
import backgroundTimer from "flow/timers/backgroundTimer";
import { InactivityEventQueue } from "flow/session/InactivityEventQueue";
import environment from "flow/configuration/environment";

function debug(...params: any[]) {
    if (environment.isEnabled(environment.keys.debugInactivity)) {
        console.debug(...params);
    }
}

// I create my own manifest
manifestManager.addManifests(config.moduleId, [
    {
        id: config.componentIds.logoutCountdownDialog,
        isDialog: true,
        name: "FlowHtml.Translations.LogOut", // Too early for meta though :/
        namespace: "default",
        reuseInstance: false,
        useFormValidation: false,
        viewModelId: "flow/viewModels/LogoutCountdownDialog",
        viewUrl: "virtual/LogoutCountdownDialog"
    }
]);

viewCache.inject("virtual/LogoutCountdownDialog", viewHtml);

export interface ILogoutCountdownViewContext {
    timeoutInSeconds: number;
    eventQueue: InactivityEventQueue;
}

function atLeastTwoDigits(value: number) {
    if (value < 10) {
        return "0" + value;
    }
    return value;
}

export default class LogoutCountdownDialog {
    componentTitle = ko.observable(this.myFlow.translate("FlowHtml.Translations.LogOut"));
    private closeCallback: CloseCallback;
    secondsElapsed = ko.observable(0);
    timeoutInSeconds = ko.observable(0);
    countdownTimer = ko.pureComputed(() => {
        if (this.timeoutInSeconds() > 0) {
            const secondsLeft = this.timeoutInSeconds() - this.secondsElapsed();
            const minutes = Math.floor(secondsLeft / 60);
            const seconds = secondsLeft % 60;
            return atLeastTwoDigits(minutes) + ":" + atLeastTwoDigits(seconds);
        } else {
            return "--:--";
        }
    });
    private timerId: number;
    commands = ko.observableArray();
    focusObservable = ko.observable();
    private eventQueue: InactivityEventQueue;
    private initialReplayDone = false;

    constructor(private readonly myFlow: MyFlow) {
        this.commands([
            {
                text: this.myFlow.translate("FlowHtml.Translations.Stay"),
                click: this.onRenew,
                focusObs: this.focusObservable
            },
            {
                text: this.myFlow.translate("FlowHtml.Translations.LogOut"),
                click: this.onLogout
            }
        ]);
    }

    activate = (viewContext: ILogoutCountdownViewContext, closeCallback: CloseCallback) => {
        this.closeCallback = closeCallback;

        this.timeoutInSeconds(viewContext.timeoutInSeconds);

        this.focusObservable(true);

        this.startTimer();

        this.eventQueue = viewContext.eventQueue;
        this.eventQueue.register(this.onEvent);
    };

    onEvent = (topic: string, payload?: any) => {
        if (topic === config.inactivityTopics.closeInactivityDialog) {
            debug("Got event, closing down countdown dialog");
            this.stopTimer();
            this.closeCallback();
        } else if (topic === config.inactivityTopics.otherTabHasUnsavedData) {
            debug("Adjusting dialog timeout based on new information");
            this.timeoutInSeconds(payload as number);
        }
    };

    onDestroy = () => {
        this.eventQueue.unregister();
    };

    startTimer = () => {
        this.timerId = backgroundTimer.setInterval(() => {
            // Need to do this after activate since Flow will perform revalDimCallback afterwards.
            // If the dialog is killed before that it will throw an exception.
            // window.requestAnimationFrame/setTimeout/setInterval don't work as they might be throttled in the tabs not in focus
            if (!this.initialReplayDone) {
                debug("Event replay");
                this.eventQueue.replayMissedEvents();
                this.initialReplayDone = true;
            }

            this.secondsElapsed(this.secondsElapsed() + 1);
            debug("Logout dialog countdown", this.secondsElapsed());

            if (this.secondsElapsed() >= this.timeoutInSeconds()) {
                debug("Logout dialog timedout", this.secondsElapsed());
                this.onLogout();
            }
        }, 1000);
    };

    stopTimer = () => {
        backgroundTimer.clearInterval(this.timerId);
    };

    onRenew = () => {
        this.stopTimer();
        this.closeCallback(true);
    };

    onLogout = () => {
        this.stopTimer();
        this.closeCallback(false);
    };
}
